import tinycolor from 'tinycolor2';

const colorGenerationSteps = [
  { name: 50, mix: ['white', 95], sat: -20, light: +2 },
  { name: 100, mix: ['white', 90], sat: -18, light: +1 },
  { name: 200, mix: ['white', 80], sat: -14, light: 0 },
  { name: 300, mix: ['white', 70], sat: -10, light: 0 },
  { name: 400, mix: ['white', 60], sat: -6, light: -1 },
  { name: 500, mix: [null, 0], sat: 0, light: 0 },
  { name: 600, mix: ['black', 10], sat: +4, light: -1 },
  { name: 700, mix: ['black', 20], sat: +8, light: -1 },
  { name: 800, mix: ['black', 30], sat: +12, light: -2 },
  { name: 900, mix: ['black', 45], sat: +16, light: -3 },
  { name: 950, mix: ['black', 60], sat: +20, light: -4 },
];

const generateColorShades = (baseColor, formatter) => {
  const startColor = tinycolor(baseColor);
  return colorGenerationSteps.reduce((shades, step) => {
    let color = startColor.clone();
    if (step.mix[0]) {
      color = tinycolor.mix(color, step.mix[0], step.mix[1]);
    }
    if (step.sat !== 0) {
      color = step.sat > 0 ? color.saturate(step.sat) : color.desaturate(-step.sat);
    }
    if (step.light !== 0) {
      color = step.light > 0 ? color.lighten(step.light) : color.darken(-step.light);
    }
    shades[step.name] = formatter(color);
    return shades;
  }, {});
};

export const generateColorShadesHex = (baseColor) => generateColorShades(baseColor, (c) => c.toHexString());

export const generateColorShadesRgb = (baseColor) => generateColorShades(baseColor, (c) => `${c._r}, ${c._g}, ${c._b}`);

export const extractThemeColors = (theme_primary_color) => {
  const hslColor = tinycolor(theme_primary_color).toHsl();
  const rgbColor = tinycolor(theme_primary_color).toRgb();

  return {
    h: hslColor.h,
    s: hslColor.s * 100,
    l: hslColor.l * 100,
    hex: theme_primary_color,
    rgb: rgbColor,
    shadesHex: generateColorShadesHex(theme_primary_color),
    shadesRgb: generateColorShadesRgb(theme_primary_color),
  };
};

export const applyThemeColors = (colorData) => {
  // Set HSL values
  document.documentElement.style.setProperty('--bulma-primary-h', colorData.h);
  document.documentElement.style.setProperty('--bulma-primary-s', `${colorData.s}%`);
  document.documentElement.style.setProperty('--bulma-primary-l', `${colorData.l}%`);

  // Set hex shades
  const hexShades = colorData.shadesHex || colorData;
  document.documentElement.style.setProperty('--p-primary-50', hexShades.hex_50 || hexShades[50]);
  document.documentElement.style.setProperty('--p-primary-100', hexShades.hex_100 || hexShades[100]);
  document.documentElement.style.setProperty('--p-primary-200', hexShades.hex_200 || hexShades[200]);
  document.documentElement.style.setProperty('--p-primary-300', hexShades.hex_300 || hexShades[300]);
  document.documentElement.style.setProperty('--p-primary-400', hexShades.hex_400 || hexShades[400]);
  document.documentElement.style.setProperty('--p-primary-500', hexShades.hex_500 || hexShades[500]);
  document.documentElement.style.setProperty('--p-primary-600', hexShades.hex_600 || hexShades[600]);
  document.documentElement.style.setProperty('--p-primary-700', hexShades.hex_700 || hexShades[700]);
  document.documentElement.style.setProperty('--p-primary-800', hexShades.hex_800 || hexShades[800]);
  document.documentElement.style.setProperty('--p-primary-900', hexShades.hex_900 || hexShades[900]);
  document.documentElement.style.setProperty('--p-primary-950', hexShades.hex_950 || hexShades[950]);

  // Set RGB shades
  const rgbShades = colorData.shadesRgb || colorData;
  document.documentElement.style.setProperty('--primary-50', rgbShades.rgb_50 || rgbShades[50]);
  document.documentElement.style.setProperty('--primary-100', rgbShades.rgb_100 || rgbShades[100]);
  document.documentElement.style.setProperty('--primary-200', rgbShades.rgb_200 || rgbShades[200]);
  document.documentElement.style.setProperty('--primary-300', rgbShades.rgb_300 || rgbShades[300]);
  document.documentElement.style.setProperty('--primary-400', rgbShades.rgb_400 || rgbShades[400]);
  document.documentElement.style.setProperty('--primary-500', rgbShades.rgb_500 || rgbShades[500]);
  document.documentElement.style.setProperty('--primary-600', rgbShades.rgb_600 || rgbShades[600]);
  document.documentElement.style.setProperty('--primary-700', rgbShades.rgb_700 || rgbShades[700]);
  document.documentElement.style.setProperty('--primary-800', rgbShades.rgb_800 || rgbShades[800]);
  document.documentElement.style.setProperty('--primary-900', rgbShades.rgb_900 || rgbShades[900]);
  document.documentElement.style.setProperty('--primary-950', rgbShades.rgb_950 || rgbShades[950]);
};

export const applyTheme = (theme_primary_color) => {
  const colorData = extractThemeColors(theme_primary_color);
  applyThemeColors(colorData);
};
